import { type ReactNode, createContext, createElement, useContext, useEffect, useLayoutEffect, useState } from 'react'

import { applyAnalyticsConsent, applyMarketingConsent, installGtagStubs } from '@/api/deferredAnalytics'
import { applyMeasurementConsent } from '@/api/tracking'
import { captureFirstTouchAttribution } from '@/lib/urlParams'
import { Sentry } from '@/sentry'

export interface CookiePreferences {
    functionality: boolean
    measurement: boolean
    marketing: boolean
}

const COOKIE_CONSENT_KEY = 'cookie-consent'
const COOKIE_PREFERENCES_KEY = 'cookie-preferences'
const COOKIE_CONSENT_TTL_DAYS = 30

const setCookie = (name: string, value: string, days: number = COOKIE_CONSENT_TTL_DAYS) => {
    const expires = new Date()
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`
}

const getCookie = (name: string): string | null => {
    const nameEQ = name + '='
    const ca = document.cookie.split(';')
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i]
        while (c.charAt(0) === ' ') c = c.substring(1, c.length)
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
    }
    return null
}

const applyTrackerConsent = (preferences: CookiePreferences) => {
    applyAnalyticsConsent(preferences.measurement)
    applyMeasurementConsent(preferences.measurement)
    applyMarketingConsent(preferences.marketing)
}

const consentCookieValue = (preferences: CookiePreferences) =>
    preferences.measurement || preferences.marketing ? 'true' : 'false'

interface CookieConsentContextValue {
    preferences: CookiePreferences
    isVisible: boolean
    isPreferencesPanelOpen: boolean
    acceptAll: () => void
    rejectAll: () => void
    savePreferences: (newPreferences: CookiePreferences) => void
    openPreferencesPanel: () => void
    closePreferencesPanel: () => void
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null)

const useCookieConsentState = (): CookieConsentContextValue => {
    const [preferences, setPreferences] = useState<CookiePreferences>({
        functionality: false,
        measurement: false,
        marketing: false,
    })
    const [isVisible, setIsVisible] = useState(false)
    const [isPreferencesPanelOpen, setIsPreferencesPanelOpen] = useState(false)

    const commitPreferences = (next: CookiePreferences) => {
        setPreferences(next)
        applyTrackerConsent(next)
    }

    useLayoutEffect(() => {
        captureFirstTouchAttribution()
    }, [])

    useEffect(() => {
        installGtagStubs()

        const savedConsent = getCookie(COOKIE_CONSENT_KEY)
        const savedPreferences = getCookie(COOKIE_PREFERENCES_KEY)

        if (savedConsent === 'true' || savedConsent === 'false') {
            setIsVisible(false)
        } else {
            setIsVisible(true)
        }

        if (savedPreferences) {
            try {
                const parsedPreferences = JSON.parse(savedPreferences) as CookiePreferences
                commitPreferences(parsedPreferences)
            } catch (error) {
                console.error('Error parsing saved cookie preferences:', error)
                Sentry.captureException(error)
            }
        }
    }, [])

    const persistDecision = (next: CookiePreferences) => {
        commitPreferences(next)
        setIsVisible(false)
        setIsPreferencesPanelOpen(false)
        setCookie(COOKIE_CONSENT_KEY, consentCookieValue(next))
        setCookie(COOKIE_PREFERENCES_KEY, JSON.stringify(next))
    }

    const acceptAll = () => {
        persistDecision({
            functionality: true,
            measurement: true,
            marketing: true,
        })
    }

    const rejectAll = () => {
        persistDecision({
            functionality: true,
            measurement: false,
            marketing: false,
        })
    }

    const savePreferences = (newPreferences: CookiePreferences) => {
        persistDecision({
            ...newPreferences,
            functionality: true,
        })
    }

    const openPreferencesPanel = () => {
        setIsPreferencesPanelOpen(true)
    }

    const closePreferencesPanel = () => {
        setIsPreferencesPanelOpen(false)
    }

    return {
        preferences,
        isVisible,
        isPreferencesPanelOpen,
        acceptAll,
        rejectAll,
        savePreferences,
        openPreferencesPanel,
        closePreferencesPanel,
    }
}

export const CookieConsentProvider = ({ children }: { children: ReactNode }) => {
    const value = useCookieConsentState()
    return createElement(CookieConsentContext.Provider, { value }, children)
}

export const useCookieConsent = () => {
    const context = useContext(CookieConsentContext)
    if (!context) {
        throw new Error('useCookieConsent must be used within CookieConsentProvider')
    }
    return context
}
